import clsx from "clsx";
import { type ReactNode, useSyncExternalStore } from "react";
import type { SuggestionStore } from "./suggestion-store";

export function SuggestionMenu<T>({
  store,
  label,
  empty,
  itemKey,
  renderItem,
}: {
  store: SuggestionStore<T>;
  label: string;
  empty: string;
  itemKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
}) {
  const s = useSyncExternalStore(store.subscribe, store.get, store.get);
  if (!s.open || (s.loading && s.items.length === 0)) return null;
  const rect = s.rect ?? { top: 0, bottom: 0, left: 0 };
  const top = Math.max(8, Math.min(rect.bottom + 6, window.innerHeight - 320));
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - 304));
  return (
    <div
      role="listbox"
      aria-label={label}
      className="fixed z-50 max-h-80 w-72 overflow-auto rounded-lg border border-line bg-surface p-1 text-sm shadow-pop"
      style={{ top, left }}
    >
      {s.items.length === 0 ? (
        <div className="px-2 py-1.5 text-ink-3">{empty}</div>
      ) : (
        s.items.map((item, i) => (
          <div
            key={itemKey(item)}
            role="option"
            tabIndex={-1}
            aria-selected={i === s.index}
            className={clsx("cursor-pointer rounded-md px-2 py-1.5", i === s.index && "bg-green-wash")}
            onMouseDown={(e) => {
              e.preventDefault();
              s.select(item);
            }}
            onMouseEnter={() => store.set({ index: i })}
          >
            {renderItem(item)}
          </div>
        ))
      )}
    </div>
  );
}
